import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Spin } from "antd";
// UUI ID
import { v4 as uuidv4 } from "uuid";
// REDUX
import { useSelector } from "react-redux";
import { usePostAllBanksBeneficialMutation } from "../../../redux/reports/reportsAPI";
// COMPONENTS
import ReportsTable from "../ReportsTable";
import ReportsHeader from "../ReportsHeader";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// INTERFACES
import { RootState } from "../../../app/store";

const ReportGeneralBeneficialUsage = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [loadPage, setLoadPage] = useState(false);
	const [customData, setCustomData] = useState<any>([]);
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [sortBy, setSortBy] = useState("");
	const [sortDirection, setSortDirection] = useState("asc");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const { theme } = useSelector((state: RootState) => state.home);
	const { filters } = useSelector((state: RootState) => state.report);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetReport, { data, isLoading, isError, error }] = usePostAllBanksBeneficialMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const generateTableRequestBody = ({ downloadReport }: any) => {
		const body: any = {
			bank_id: filters.bankId,
			start_date: filters.startDate,
			end_date: filters.endDate,
			timezone: filters.timezoneCode,
			exclusions: {
				saturdays: filters.exclusionSaturdays ? "true" : "false",
				sundays: filters.exclusionSundays ? "true" : "false",
				holidays: filters.exclusionHolidays ? "true" : "false",
				time_ranges: filters.exclusionTimeList.join(","),
				date_ranges: filters.exclusionDateList.join(","),
			},
			download: downloadReport,
			page: currentPage,
			items_per_page: pageSize,
			sort_by: sortBy,
			sort_direction: sortDirection,
		};
		return body;
	};

	const generateCustomData = (results: any) => {
		const newData = results.results.map((item: any) => ({
			key: uuidv4(),
			...item,
		}));

		setCustomData({
			results: newData,
			status: results.status,
			total: results.total,
		});
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		setLoadPage(true);
		setTimeout(() => {
			setLoadPage(false);
		}, 1000);
	}, []);

	useEffect(() => {
		if (data && data.results) {
			generateCustomData(data);
		}
	}, [data]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Spin spinning={loadPage}>
			{validatePermission("view-report-management-general-beneficial-usage", permissions) ? (
				<div className={`layout__contentPage__${theme}`}>
					<ReportsHeader
						reportDefaultData={{
							reportType: "REPORT",
							levelTypeName: "BANKS",
							displaySelectTypeLevel: false,
							liftType: "ALL",
							defaultColumns: [
								"bank_name",
								"lift_name",
								"lift_type",
								"start_date",
								"end_date",
								"availability",
								"unavailability",
								"total_interruptions",
							],
							defaultOffsets: [],
							infoDescriptionText: "report.infoDescriptionText2",
							displayDownloadCSVReport: true,
							displayDownloadPDFReport: true,
							reportTitle: "General / Beneficial Usage",
							displayAdvanceFiltersExclusions: true,
							displayAdvanceFiltersOffsets: false,
							disabledOffsets: true,
							useTablePagination: true,
							displayDateByRange: false,
							displayDate: true,
							displaySelectLiftType: false,
							displayAdvanceFilterCharts: false,
							displayAdvanceFiltersTable: false,
							displayRecentWeeksPicket: false,
							displayInputLimitItems: false,
							displayChartMirror: false,
							chartMirror: false,
							chartType: "bar_stack_vertical",
							dateTypeOptions: [
								{ id: "TODAY", name: "Day" },
								{ id: "WEEK", name: "Week" },
								{ id: "MONTH", name: "Month" },
								{ id: "QUARTER", name: "Quarter" },
								{ id: "YEAR", name: "Year" },
							],
							dateDaysLimit: 0,
							displayMinimumActivation: false,
							dateWeeksLimit: 0,
							dateMonthsLimit: 0,
							dateQuarterLimit: 0,
							dateYearsLimit: 0,
							dateType: "MONTH",
							bankSearchAndFilterString: "",
						}}
					/>
					<ReportsTable
						response={customData}
						generateTableRequestBody={generateTableRequestBody}
						csvUrl={`${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/reports/all-banks/beneficial`}
						displayExpandibleRowTable
						groupColumns={null}
						triggerGetReport={triggerGetReport}
						isLoading={isLoading}
						isError={isError}
						error={error}
						updatePageSize={setPageSize}
						updateCurrentPage={setCurrentPage}
						sortBy={setSortBy}
						sortDirection={setSortDirection}
					/>
				</div>
			) : (
				<UnauthorizedPage />
			)}
		</Spin>
	);
};

export default ReportGeneralBeneficialUsage;
